import * as React from 'react';
import { format } from 'date-fns';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { useTenant } from '@/hooks';
import {
  useAddOrUpdateMilestoneMutation,
  useUpdateMilestoneMutation,
  useCreateExternalMilestoneMutation,
  useUpdateExternalMilestoneMutation,
} from '@/state';
import type { MilestoneEditorValue } from '@/types';
import { RichTextEditor } from '@/components/rich-text';
import { AxiosError } from 'axios';

export function MilestoneUpsertDialog({
  open,
  onOpenChange,
  projectId,
  initial,
  isExternal,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: number;
  initial?: MilestoneEditorValue | null; // if present => edit
  isExternal?: boolean;
}) {
  const { tenant } = useTenant();
  const [mutate, { isLoading: isMutatingInternal }] = useAddOrUpdateMilestoneMutation();
  const [update] = useUpdateMilestoneMutation();
  const [createExternal, { isLoading: isCreatingExternal }] = useCreateExternalMilestoneMutation();
  const [updateExternal, { isLoading: isUpdatingExternal }] = useUpdateExternalMilestoneMutation();

  const isLoading = isMutatingInternal || isCreatingExternal || isUpdatingExternal;

  const isEdit = Boolean(initial?.id);

  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [outcome, setOutcome] = React.useState('');
  const [dueDate, setDueDate] = React.useState<Date | undefined>(undefined);

  React.useEffect(() => {
    if (!open) return;

    setTitle(initial?.milestones ?? '');
    setDescription(initial?.milestone_description ?? '');
    setOutcome(initial?.milestone_outcome ?? '');
    setDueDate(initial?.end_date ? new Date(initial.end_date) : undefined);
  }, [open, initial]);

  const canSubmit = title.trim().length > 0 && Boolean(dueDate);

  const handleSubmit = async () => {
    if (!isExternal && !tenant) return toast.message('Tenant missing');
    if (!canSubmit) return toast.message('Please add a title and due date');

    try {
      if (isEdit) {
        if (isExternal) {
          await updateExternal({
            id: initial?.id!,
            data: {
              milestones: title.trim(),
              milestone_description: description?.trim()!,
              milestone_outcome: outcome.trim(),
              end_date: dueDate!.toISOString(),
              project: projectId,
            },
          }).unwrap();
        } else {
          await update({
            tenant: tenant!,
            id: initial?.id!,
            data: {
              milestones: title.trim(),
              milestone_description: description?.trim()!,
              end_date: dueDate!.toISOString(),
              project: projectId,
            },
          });
        }
      } else {
        if (isExternal) {
          await createExternal({
            data: {
              milestones: title.trim(),
              milestone_description: description?.trim()!,
              milestone_outcome: outcome.trim(),
              end_date: dueDate!.toISOString(),
              project: projectId,
            },
          }).unwrap();
        } else {
          await mutate({
            tenant: tenant!,
            data: {
              milestones: title.trim(),
              milestone_description: description?.trim()!,
              end_date: dueDate!.toISOString(),
              project: projectId,
            },
          }).unwrap();
        }
      }

      toast.success(isEdit ? 'Milestone updated' : 'Milestone created');
      onOpenChange(false);
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.message ?? 'Failed to create milestone');
      } else {
        toast.error('Failed to create milestone');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'sm:max-w-xl border-border/40 bg-background/95 backdrop-blur-xl shadow-2xl p-0 overflow-hidden'
        )}
      >
        <DialogHeader className="p-8 pb-4">
          <DialogTitle className="text-2xl font-bold tracking-tight bg-gradient-to-br from-primary to-primary/60 bg-clip-text text-transparent">
            {isEdit ? 'Edit Milestone' : 'Create Milestone'}
          </DialogTitle>
          <p className="text-sm text-muted-foreground font-medium">
            Keep it outcome-based and time-bound.
          </p>
        </DialogHeader>

        <div className="p-8 pt-2 space-y-6 overflow-y-auto max-h-[70vh] custom-scrollbar">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-tight ml-1">
              Milestone Title *
            </Label>
            <Input
              placeholder="e.g. Phase 1 Completion"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {isExternal && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-tight ml-1">
                Milestone Outcome
              </Label>
              <Input
                placeholder="e.g. Project Plan Document"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-tight ml-1">
              Description
            </Label>

            <div className="rounded-[1.5rem] border border-border/40 bg-muted/20 overflow-hidden ring-offset-background focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <RichTextEditor
                value={description}
                minHeight={200}
                onChange={(e) => setDescription(e)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-tight ml-1">
              Due Date *
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    ' justify-start text-left font-normal w-full',
                    'bg-background/60 backdrop-blur',
                    !dueDate && 'text-muted-foreground'
                  )}
                >
                  {dueDate ? format(dueDate, 'PPP') : 'Pick a due date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0  overflow-hidden">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter className="p-8 pt-4 border-t border-border/20 bg-muted/10">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit || isLoading} onClick={handleSubmit}>
            {isLoading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Milestone'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}